import { useEffect, useRef, useState, type RefObject } from "react";
import { usePetsWithOwners, type OwnerWithPetsRow } from "@/hooks/usePetsWithOwners";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@akuna/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import { AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { useTranslation } from "react-i18next";

import { Plus, Phone, Mail, MapPin, MoreVertical, Eye, Edit, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { differenceInYears, formatDistanceToNow, intervalToDuration } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import type { Locale } from "date-fns";
import { Link, useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { AddressLink } from "@/components/shared/AddressLink";
import { cn } from "@/lib/utils";
import { parseISODateOrDateTime } from "@/lib/date-utils";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { isPetDeceased } from "@/lib/utils/pet-death";
import { SearchField } from "@akuna/ui";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";

const getPatientRowKey = (row: OwnerWithPetsRow) =>
  row.pet ? `${row.owner.id}-${row.pet.id}` : `${row.owner.id}-no-pets`;

const mergeUniquePatientRows = (
  previousRows: OwnerWithPetsRow[],
  nextRows: OwnerWithPetsRow[],
) => {
  const seenKeys = new Set(previousRows.map(getPatientRowKey));
  const mergedRows = [...previousRows];

  for (const row of nextRows) {
    const key = getPatientRowKey(row);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      mergedRows.push(row);
    }
  }

  return mergedRows;
};

const EMPTY_PATIENT_ROWS: OwnerWithPetsRow[] = [];

const getIsMobileViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(max-width: 767px)").matches;

function PatientPetActionsMenu({
  ownerId,
  petId,
  t,
}: {
  ownerId: string;
  petId: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">{t("menu.open")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t("menu.actions")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={`/patients/pets/${petId}`} className="cursor-pointer">
            <Eye className="mr-2 h-4 w-4" />
            {t("menu.viewPet")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/patients/pets/${petId}/edit`} className="cursor-pointer">
            <Edit className="mr-2 h-4 w-4" />
            {t("menu.editPet")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={`/patients/owners/${ownerId}`} className="cursor-pointer">
            <Eye className="mr-2 h-4 w-4" />
            {t("menu.viewOwner")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/patients/owners/${ownerId}/edit`} className="cursor-pointer">
            <Edit className="mr-2 h-4 w-4" />
            {t("menu.editOwner")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PatientOwnerActionsMenu({
  ownerId,
  t,
}: {
  ownerId: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">{t("menu.open")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t("menu.actions")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={`/patients/owners/${ownerId}`} className="cursor-pointer">
            <Eye className="mr-2 h-4 w-4" />
            {t("menu.viewOwner")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/patients/owners/${ownerId}/edit`} className="cursor-pointer">
            <Edit className="mr-2 h-4 w-4" />
            {t("menu.editOwner")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PatientsTableLoading() {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead><Skeleton className="h-4 w-20" /></TableHead>
            <TableHead><Skeleton className="h-4 w-24" /></TableHead>
            <TableHead><Skeleton className="h-4 w-20" /></TableHead>
            <TableHead><Skeleton className="h-4 w-24" /></TableHead>
            <TableHead className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableHead>
            <TableHead className="w-[70px]"><Skeleton className="ml-auto h-4 w-4" /></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 6 }).map((_, index) => (
            <TableRow key={`patients-skeleton-${index}`} className="hover:bg-transparent">
              <TableCell>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <Skeleton className="h-8 w-8 rounded-md" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PatientsMobileCardsLoading({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3 md:hidden">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={`patients-mobile-skeleton-${index}`} className="rounded-2xl border-border/60 shadow-sm">
          <CardContent className="space-y-4 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-2xl" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
            <div className="border-t pt-4 space-y-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-36" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-44" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PatientsMobileCard({
  row,
  dateLocale,
  formatLocalizedPetAge,
  t,
}: {
  row: OwnerWithPetsRow;
  dateLocale: Locale;
  formatLocalizedPetAge: (date: Date | string | null | undefined) => string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const navigate = useNavigate();
  const { owner, pet, petCount } = row;
  const primaryPhone = owner.phones?.find((phone) => phone.is_primary) || owner.phones?.[0];
  const primaryEmail = owner.emails?.find((email) => email.is_primary) || owner.emails?.[0];
  const primaryAddress = owner.primaryAddress;
  const createdAt = formatDistanceToNow(new Date(pet?.created_at ?? owner.created_at), {
    addSuffix: true,
    locale: dateLocale,
  });

  const petTarget = pet ? `/patients/pets/${pet.id}` : null;

  return (
    <div className="block">
      <Card
        className="overflow-hidden rounded-2xl border-border/60 bg-card shadow-sm md:hidden"
        onClick={petTarget ? () => navigate(petTarget) : undefined}
        style={petTarget ? { cursor: "pointer" } : undefined}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Avatar className="h-12 w-12 shrink-0 rounded-2xl">
              {pet?.photo?.file_path ? (
                <AvatarImage src={pet.photo.file_path} alt={pet.name} />
              ) : null}
              <AvatarFallback className="rounded-2xl bg-orange-100 text-orange-600">
                <CustomIcons name="paws" className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              {pet ? (
                <>
                  <div className="flex min-w-0 items-center gap-2">
                    <Link
                      to={`/patients/pets/${pet.id}`}
                      className="truncate font-semibold text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {pet.name}
                    </Link>
                    {pet.record_number ? (
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        #{pet.record_number}
                      </span>
                    ) : null}
                    {isPetDeceased(pet) && pet.status?.name ? (
                      <Badge variant="outline" className="shrink-0">
                        {pet.status.name}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="mt-1 flex min-w-0 items-center gap-2 text-sm">
                    {pet.breed?.name ? (
                      <span className="truncate font-medium text-foreground/80">{pet.breed.name}</span>
                    ) : null}
                    {pet.birth_date && pet.breed?.name ? (
                      <span className="h-1 w-1 shrink-0 rounded-full bg-border" />
                    ) : null}
                    {pet.birth_date ? (
                      <span className="shrink-0 text-muted-foreground">
                        {formatLocalizedPetAge(pet.birth_date)}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm italic text-muted-foreground">{t("ownerWithoutPet")}</p>
                  <Link
                    to={`/patients/owners/${owner.id}`}
                    className="mt-1 block truncate font-semibold text-primary hover:underline"
                  >
                    {owner.name}
                  </Link>
                </>
              )}
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              {pet ? (
                <PatientPetActionsMenu ownerId={owner.id} petId={pet.id} t={t} />
              ) : (
                <PatientOwnerActionsMenu ownerId={owner.id} t={t} />
              )}
            </div>
          </div>

          <div className="mt-4 border-t border-border/70 pt-4">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CustomIcons name="user" className="h-4 w-4" />
              </div>
              <Link
                to={`/patients/owners/${owner.id}`}
                className="min-w-0 truncate text-sm font-semibold text-primary"
                onClick={(e) => e.stopPropagation()}
              >
                {owner.name}
              </Link>
              <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {pet ? t("petsCount", { count: petCount }) : t("ownerWithoutRegisteredPets")}
              </span>
            </div>

            <div className="mt-4 space-y-2.5 text-sm">
              {primaryPhone ? (
                <div className="flex items-center gap-2 text-foreground/80">
                  <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate tabular-nums">{primaryPhone.phone_number}</span>
                </div>
              ) : null}
              {primaryAddress ? (
                <div className="flex items-start gap-2 text-foreground/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <AddressLink addressText={primaryAddress}>
                    <span className="line-clamp-2 leading-5">{primaryAddress}</span>
                  </AddressLink>
                </div>
              ) : null}
              {primaryEmail ? (
                <div className="flex items-center gap-2 text-foreground/80">
                  <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{primaryEmail.email}</span>
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>{t("mobile.createdAt", { date: createdAt })}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PatientsMobileEmptyState({
  searchTerm,
  debouncedSearchTerm,
  t,
}: {
  searchTerm: string;
  debouncedSearchTerm: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <Card className="rounded-2xl border-border/60 shadow-sm md:hidden">
      <CardContent className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <div className="rounded-full bg-muted p-4">
          <CustomIcons name="paws" className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <p className="font-medium text-foreground">
            {debouncedSearchTerm ? t("emptyState.searchTitle") : t("emptyState.defaultTitle")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {searchTerm ? t("emptyState.searchDescription") : t("emptyState.defaultDescription")}
          </p>
        </div>
        {!searchTerm && (
          <Button size="sm" className="mt-2" asChild>
            <Link to="/patients/new?mode=create">
              <Plus className="mr-2 h-4 w-4" />
              {t("emptyState.createOwner")}
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function PatientsMobileListFooter({
  hasRows,
  hasMoreRows,
  isLoadingMore,
  loadMoreRef,
  t,
}: {
  hasRows: boolean;
  hasMoreRows: boolean;
  isLoadingMore: boolean;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (!hasRows) return <div ref={loadMoreRef} className="h-1 md:hidden" />;

  return (
    <div ref={loadMoreRef} className="py-3 text-center text-xs text-muted-foreground md:hidden">
      {isLoadingMore ? (
        <div className="flex items-center justify-center gap-2">
          <Skeleton className="h-2 w-2 rounded-full" />
          <span>{t("mobile.loadingMore")}</span>
        </div>
      ) : hasMoreRows ? (
        <span className="sr-only">{t("mobile.loadingMore")}</span>
      ) : (
        <span>{t("mobile.endOfResults")}</span>
      )}
    </div>
  );
}

function PatientsPageLoading() {
  return (
    <div className="space-y-6">
      <div className="flex md:flex-row flex-col gap-4 md:gap-0 md:items-center md:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="flex md:flex-row flex-col gap-2">
          <Skeleton className="h-10 w-full md:w-40" />
          <Skeleton className="h-10 w-full md:w-36" />
        </div>
      </div>
      <Skeleton className="h-10 w-full" />
      <Card>
        <CardContent className="p-0">
          <PatientsTableLoading />
        </CardContent>
      </Card>
    </div>
  );
}

export default function Patients() {
  const { t, i18n } = useTranslation("patients");
  const { t: tCommon } = useTranslation("common");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 350);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobilePage, setMobilePage] = useState(1);
  const [mobileRows, setMobileRows] = useState<OwnerWithPetsRow[]>([]);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const mobileLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const pageSize = 25;
  const dateLocale: Locale = i18n.language.startsWith("en") ? enUS : ptBR;

  // Verificar permissão para patients (unifica owners e pets)
  const { hasPermission: hasPatientsView, isLoading: isLoadingPatients } = useHasPermissionSync("patients", "view");

  const isLoadingPermissions = isLoadingPatients;
  const hasAccess = hasPatientsView;
  const requestPage = isMobileViewport ? mobilePage : currentPage;

  // Buscar owners com pets (paginado) - debounced search + keepPreviousData no hook evitam flicker e requests por tecla
  const {
    data: rowsData,
    isFetching: isFetchingData,
    isLoading: isLoadingData,
    isPlaceholderData,
  } = usePetsWithOwners(
    debouncedSearchTerm,
    requestPage,
    pageSize,
    { enabled: hasAccess && !isLoadingPermissions }
  );

  // Extrair dados paginados
  const rows = rowsData?.data || EMPTY_PATIENT_ROWS;

  const totalCount = rowsData?.totalCount || 0;
  const totalPages = rowsData?.totalPages || 0;
  const hasCurrentRequestPageData =
    !isPlaceholderData && rowsData?.currentPage === requestPage;
  const hasCurrentMobilePageData =
    !isPlaceholderData && rowsData?.currentPage === mobilePage;
  const mobileVisibleRows = mobileRows.length > 0 || mobilePage > 1 || !hasCurrentMobilePageData ? mobileRows : rows;
  const desktopVisibleRows = hasCurrentRequestPageData ? rows : EMPTY_PATIENT_ROWS;
  const hasMoreMobileRows = totalPages > 0 && mobilePage < totalPages && mobileVisibleRows.length < totalCount;
  const isLoadingInitialMobileRows =
    (isLoadingData || isFetchingData || !hasCurrentMobilePageData) &&
    mobilePage === 1 &&
    mobileVisibleRows.length === 0;
  const isLoadingMoreMobileRows = isFetchingData && mobilePage > 1;
  const visibleRows = isMobileViewport ? mobileVisibleRows : desktopVisibleRows;
  const isLoadingInitialRows = isMobileViewport
    ? isLoadingInitialMobileRows
    : (isLoadingData || isFetchingData || !hasCurrentRequestPageData) && desktopVisibleRows.length === 0;

  const getPageItems = (current: number, total: number) => {
    if (total <= 1) return [1];
    const pages = new Set<number>([1, total]);
    for (let page = current - 1; page <= current + 1; page += 1) {
      if (page >= 1 && page <= total) pages.add(page);
    }
    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    const items: Array<number | "ellipsis"> = [];
    let lastPage = 0;
    for (const page of sortedPages) {
      if (lastPage && page - lastPage > 1) items.push("ellipsis");
      items.push(page);
      lastPage = page;
    }
    return items;
  };

  const formatLocalizedPetAge = (date: Date | string | null | undefined) => {
    if (!date) return null;

    const birthDate = typeof date === "string" ? parseISODateOrDateTime(date) : date;
    if (!birthDate || Number.isNaN(birthDate.getTime())) return null;

    const now = new Date();
    const years = differenceInYears(now, birthDate);

    if (years >= 1) {
      return t("age.years", { count: years });
    }

    const { months, days } = intervalToDuration({
      start: birthDate,
      end: now,
    });

    if ((months ?? 0) >= 1) {
      return t("age.months", { count: months ?? 0 });
    }

    return t("age.days", { count: days ?? 0 });
  };

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const updateIsMobileViewport = () => {
      setIsMobileViewport(getIsMobileViewport());
    };

    updateIsMobileViewport();
    mediaQuery.addEventListener("change", updateIsMobileViewport);

    return () => {
      mediaQuery.removeEventListener("change", updateIsMobileViewport);
    };
  }, []);

  useEffect(() => {
    setMobilePage(1);
    setMobileRows([]);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!isMobileViewport || isFetchingData || !hasCurrentMobilePageData || rows.length === 0) return;

    setMobileRows((previousRows) => {
      if (mobilePage === 1) {
        return rows;
      }

      return mergeUniquePatientRows(previousRows, rows);
    });
  }, [hasCurrentMobilePageData, isFetchingData, isMobileViewport, mobilePage, rows]);

  useEffect(() => {
    if (!isMobileViewport || !hasMoreMobileRows || isFetchingData || !hasCurrentMobilePageData) return;

    const node = mobileLoadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting) {
          observer.disconnect();
          setMobilePage((page) => Math.min(page + 1, totalPages));
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasCurrentMobilePageData, hasMoreMobileRows, isFetchingData, isMobileViewport, totalPages]);

  // Funções de navegação
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Resetar página quando a busca mudar
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setMobilePage(1);
    setMobileRows([]);
  };

  useMobileHeader({
    title: t("patients", { ns: "nav" }),
    meta: (
      <span className="flex items-center gap-2">
        <span className="h-[0.4375rem] w-[0.4375rem] rounded-full bg-[var(--state-success)]" />
        <span>
          <b className="text-[var(--fg-default)]">{totalCount}</b> {t("patientsActive", { ns: "nav" })}
        </span>
      </span>
    ),
    search: (
      <SearchField
        clearLabel={tCommon("clearSearch")}
        label={tCommon("search")}
        onChange={(e) => handleSearchChange(e.target.value)}
        onClear={() => handleSearchChange("")}
        placeholder={t("searchPlaceholder")}
        value={searchTerm}
      />
    ),
  }, [totalCount, searchTerm, t, tCommon]);

  // Se ainda está carregando permissões, mostrar loading
  if (isLoadingPermissions) {
    return <PatientsPageLoading />;
  }

  // Se não tem acesso, mostrar mensagem
  if (!hasAccess) {
    return (
      <Alert variant="danger">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("accessDeniedTitle")}</AlertTitle>
        <AlertDescription>
          {t("accessDeniedDescription")}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="hidden flex-col gap-4 md:flex md:flex-row md:items-center md:justify-between md:gap-0">
        <div>
          <h1 className="text-3xl font-bold text-color-new-title">{t("patients", { ns: "nav" })}</h1>
          <p className="mt-1 text-sm text-muted-foreground md:hidden">
            {t("mobile.activeResults", { count: totalCount })}
          </p>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-2 md:flex md:flex-row">
          <Button variant="outline" className="h-10 min-w-0 overflow-hidden rounded-full px-4 text-sm" asChild>
            <Link to="/patients/new?mode=create">
              <CustomIcons name="user" className="!h-5 !w-5" />
              <span className="min-w-0 truncate">{t("createOwner")}</span>
            </Link>
          </Button>
          <Button className="h-10 min-w-0 overflow-hidden rounded-full px-4 text-sm" asChild>
            <Link to="/patients/new">
              <Plus className="!h-5 !w-5" />
              <span className="min-w-0 truncate">{t("createPatient")}</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Barra de busca */}
      <div className="hidden items-center gap-4 md:flex">
        <div className="flex-1">
          <SearchField
            clearLabel={tCommon("clearSearch")}
            label={tCommon("search")}
            placeholder={t("searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onClear={() => handleSearchChange("")}
          />
        </div>
      </div>

      {/* Tabela de tutores */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("table.pet")}</TableHead>
                  <TableHead>{t("table.breedAge")}</TableHead>
                  <TableHead>{t("table.owner")}</TableHead>
                  <TableHead>{t("table.contact")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("table.createdAt")}</TableHead>
                  <TableHead className="w-[70px]">{t("table.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingInitialRows ? (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <PatientsTableLoading />
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length > 0 ? (
                  <>
                    {visibleRows.map((row, index) => {
                      const { owner, pet, petCount } = row;
                      const isLastRow = index === visibleRows.length - 1;
                      const primaryPhone = owner.phones?.find((p) => p.is_primary) || owner.phones?.[0];
                      const primaryEmail = owner.emails?.find((e) => e.is_primary) || owner.emails?.[0];
                      const primaryAddress = owner.primaryAddress;

                      // Se não tem pet, renderizar linha de owner sem pet
                      if (!pet) {
                        return (
                          <TableRow key={`${owner.id}-no-pets`} className="hover:bg-muted/50">
                            {/* Coluna PET */}
                            <TableCell className={cn(
                              isLastRow && "rounded-bl-lg"
                            )}>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-muted/50">
                                    <CustomIcons name="paws" className="h-5 w-5 text-muted-foreground/50" />
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="text-sm text-muted-foreground italic">
                                    {t("ownerWithoutPet")}
                                  </p>
                                </div>
                              </div>
                            </TableCell>

                            {/* Coluna RAÇA / IDADE */}
                            <TableCell>
                              <p className="text-sm text-muted-foreground"></p>
                            </TableCell>

                            {/* Coluna TUTOR */}
                            <TableCell>
                              <div>
                                <Link
                                  to={`/patients/owners/${owner.id}`}
                                  className="font-medium text-primary hover:underline"
                                >
                                  {owner.name}
                                </Link>
                                <p className="text-sm text-muted-foreground">
                                  {t("ownerWithoutRegisteredPets")}
                                </p>
                              </div>
                            </TableCell>

                            {/* Coluna CONTATO */}
                            <TableCell>
                              <div className="space-y-1 text-sm">
                                {primaryPhone && (
                                  <div className="flex items-center gap-2 text-muted-foreground">
                                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{primaryPhone.phone_number}</span>
                                  </div>
                                )}
                                {primaryAddress && (
                                  <div className="flex items-start gap-2 text-muted-foreground">
                                    <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                    <AddressLink addressText={primaryAddress}>
                                      <span className="line-clamp-2">{primaryAddress}</span>
                                    </AddressLink>
                                  </div>
                                )}
                                {primaryEmail && (
                                  <div className="flex items-center gap-2 text-muted-foreground">
                                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate max-w-[200px]">{primaryEmail.email}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            {/* Coluna CADASTRO */}
                            <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(owner.created_at), {
                                addSuffix: true,
                                locale: dateLocale,
                              })}
                            </TableCell>

                            {/* Coluna AÇÕES */}
                            <TableCell className={cn(
                              isLastRow && "rounded-br-lg"
                            )}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                    <span className="sr-only">{t("menu.open")}</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuLabel>{t("menu.actions")}</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem asChild>
                                    <Link to={`/patients/owners/${owner.id}`} className="cursor-pointer">
                                      <Eye className="mr-2 h-4 w-4" />
                                      {t("menu.viewOwner")}
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem asChild>
                                    <Link to={`/patients/owners/${owner.id}/edit`} className="cursor-pointer">
                                      <Edit className="mr-2 h-4 w-4" />
                                      {t("menu.editOwner")}
                                    </Link>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      // Renderizar linha com pet

                      return (
                        <TableRow key={`${owner.id}-${pet.id}`} className="hover:bg-muted/50">
                          {/* Coluna PET */}
                          <TableCell className={cn(
                            isLastRow && "rounded-bl-lg"
                          )}>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-12 w-12">
                                <AvatarImage
                                  src={pet?.photo?.file_path}
                                  alt={pet.name}
                                />
                                <AvatarFallback className="bg-orange-100 text-orange-600">
                                  <CustomIcons name="paws" className="h-5 w-5" />
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-2">
                                  <Link
                                    to={`/patients/pets/${pet.id}`}
                                    className="font-medium text-left text-primary hover:underline"
                                  >
                                    {pet.name}
                                  </Link>
                                  {isPetDeceased(pet) && pet.status?.name ? (
                                    <Badge variant="outline" >
                                      {pet.status.name}
                                    </Badge>
                                  ) : null}
                                </div>
                                {
                                  pet.record_number
                                    ? (
                                      <p className="text-sm text-muted-foreground/80">
                                        #{pet.record_number}
                                      </p>
                                    ) : null
                                }
                              </div>
                            </div>
                          </TableCell>

                          {/* Coluna RAÇA / IDADE */}
                          <TableCell>
                            <div>
                              {pet.breed?.name && (
                                <p className="font-medium text-muted-foreground">
                                  {pet.breed.name}
                                </p>
                              )}
                              {pet.birth_date && (
                                <p className="text-sm text-muted-foreground/80">
                                  {formatLocalizedPetAge(pet.birth_date)}
                                </p>
                              )}
                            </div>
                          </TableCell>

                          {/* Coluna TUTOR */}
                          <TableCell>
                            <div>
                              <Link
                                to={`/patients/owners/${owner.id}`}
                                className="font-medium text-primary hover:underline"
                              >
                                {owner.name}
                              </Link>
                              <p className="text-sm text-muted-foreground">
                                {t("petsCount", { count: petCount })}
                              </p>
                            </div>
                          </TableCell>

                          {/* Coluna CONTATO */}
                          <TableCell>
                            <div className="space-y-1 text-sm">
                              {primaryPhone && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span className="truncate">{primaryPhone.phone_number}</span>
                                </div>
                              )}
                              {primaryAddress && (
                                <div className="flex items-start gap-2 text-muted-foreground">
                                  <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                  <AddressLink addressText={primaryAddress}>
                                    <span className="line-clamp-2">{primaryAddress}</span>
                                  </AddressLink>
                                </div>
                              )}
                              {primaryEmail && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span className="truncate max-w-[200px]">{primaryEmail.email}</span>
                                </div>
                              )}
                            </div>
                          </TableCell>

                          {/* Coluna CADASTRO */}
                          <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(pet.created_at), {
                              addSuffix: true,
                              locale: dateLocale,
                            })}
                          </TableCell>

                          {/* Coluna AÇÕES */}
                          <TableCell className={cn(
                            isLastRow && "rounded-br-lg"
                          )}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                  <span className="sr-only">{t("menu.open")}</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuLabel>{t("menu.actions")}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                  <Link to={`/patients/pets/${pet.id}`} className="cursor-pointer">
                                    <Eye className="mr-2 h-4 w-4" />
                                    {t("menu.viewPet")}
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/patients/pets/${pet.id}/edit`} className="cursor-pointer">
                                    <Edit className="mr-2 h-4 w-4" />
                                    {t("menu.editPet")}
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                  <Link to={`/patients/owners/${owner.id}`} className="cursor-pointer">
                                    <Eye className="mr-2 h-4 w-4" />
                                    {t("menu.viewOwner")}
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/patients/owners/${owner.id}/edit`} className="cursor-pointer">
                                    <Edit className="mr-2 h-4 w-4" />
                                    {t("menu.editOwner")}
                                  </Link>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className={cn(
                      "text-center py-12",
                      "rounded-tl-lg rounded-tr-lg rounded-bl-lg rounded-br-lg"
                    )}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="rounded-full bg-muted p-4">
                          <CustomIcons name="paws" className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {debouncedSearchTerm
                              ? t("emptyState.searchTitle")
                              : t("emptyState.defaultTitle")}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {searchTerm
                              ? t("emptyState.searchDescription")
                              : t("emptyState.defaultDescription")}
                          </p>
                        </div>
                        {!searchTerm && (
                          <Button
                            size="sm"
                            className="mt-2"
                            asChild
                          >
                            <Link to="/patients/new?mode=create">
                              <Plus className="h-4 w-4 mr-2" />
                              {t("emptyState.createOwner")}
                            </Link>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3 pb-44 md:hidden">
        {isLoadingInitialMobileRows ? (
          <PatientsMobileCardsLoading />
        ) : mobileVisibleRows.length > 0 ? (
          <>
            {mobileVisibleRows.map((row) => (
              <PatientsMobileCard
                key={getPatientRowKey(row)}
                row={row}
                dateLocale={dateLocale}
                formatLocalizedPetAge={formatLocalizedPetAge}
                t={t}
              />
            ))}
            <PatientsMobileListFooter
              hasRows={mobileVisibleRows.length > 0}
              hasMoreRows={hasMoreMobileRows}
              isLoadingMore={isLoadingMoreMobileRows}
              loadMoreRef={mobileLoadMoreRef}
              t={t}
            />
          </>
        ) : (
          <PatientsMobileEmptyState
            searchTerm={searchTerm}
            debouncedSearchTerm={debouncedSearchTerm}
            t={t}
          />
        )}
      </div>

      <div className="fixed bottom-[calc(96px+env(safe-area-inset-bottom))] right-4 z-40 md:hidden">
        <Button
          className="h-12 gap-2 rounded-full px-5 text-[15px] font-bold shadow-xl shadow-primary/25"
          onClick={() => setIsCreateSheetOpen(true)}
        >
          <Plus className="h-[18px] w-[18px]" />
          {t("create")}
        </Button>
      </div>

      <Sheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>{t("createSheetTitle")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-2 pb-[env(safe-area-inset-bottom)]">
            <Button
              asChild
              className="h-12 justify-start gap-2 rounded-full px-5 text-[15px] font-bold"
            >
              <Link to="/patients/new?mode=create" onClick={() => setIsCreateSheetOpen(false)}>
                <CustomIcons name="user" className="!h-4 !w-4 [&_path]:stroke-primary-foreground" />
                {t("createOwner")}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-12 justify-start gap-2 rounded-full border-border bg-card px-5 text-[15px] font-bold"
            >
              <Link to="/patients/new" onClick={() => setIsCreateSheetOpen(false)}>
                <Plus className="h-[18px] w-[18px]" />
                {t("createPatient")}
              </Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Controles de paginação */}
      <div className="hidden md:flex md:flex-row flex-col gap-2 md:gap-0 items-center justify-between px-4 py-4 border rounded-lg bg-muted/30 ">
        <div className="text-sm text-muted-foreground">
          {t("pagination.showingResults", {
            start: ((currentPage - 1) * pageSize) + 1,
            end: Math.min(currentPage * pageSize, totalCount),
            total: totalCount,
          })}
        </div>

        {totalPages > 1 && (
          <div className="flex md:flex-row flex-col gap-2 md:gap-0 items-center space-x-0 md:space-x-2 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="w-full md:w-auto"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("pagination.previous")}
            </Button>

            <div className="flex items-center flex-wrap md:flex-nowrap gap-1 md:gap-0 space-x-0 md:space-x-1">
              {getPageItems(currentPage, totalPages).map((item, index) =>
                item === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-sm text-muted-foreground">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    variant={currentPage === item ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(item)}
                    className="min-w-8 h-8 p-0 px-2"
                  >
                    {item}
                  </Button>
                ),
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="w-full md:w-auto"
            >
              {t("pagination.next")}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
